import { clone } from 'lodash'
import type Session from '@/assets/ts/interfaces/Session'
import type Workout from '@/assets/ts/interfaces/Workout'
import type WorkoutWeekStore from '@/assets/ts/interfaces/WorkoutWeekStore'

const { getWorkoutWeek } = useWorkoutWeek()

const initialWorkoutWeek = {
  workoutWeekNumber: 0,
  moveToToday: 0,
  completedSessions: [],
  remainingWorkouts: []
}

const useStoreWorkoutWeek = defineStore( 'workoutWeekStore', () => {
  const programStore = useStoreProgramNew()
  const sessionStore = useStoreSession()
  const profileStore = useStoreProfile()
  const workoutWeek = useStorage<WorkoutWeekStore>( 'workoutWeek', clone( initialWorkoutWeek ) )

  const fetchWorkoutWeekFromDatabase = async ( memberId: number ) => {
    try {
      const response = await getWorkoutWeek( memberId )
      console.log( 'Fetched workout week:', response )
      workoutWeek.value = response
      console.log( 'workoutWeek:', workoutWeek.value )
    } catch ( error ) {
      console.error( 'Failed to fetch workout week:', error )
    }
  }

  const initializeWorkoutWeek = async ( memberId: number ) => {
    if ( ! workoutWeek.value.workoutWeekNumber ) {
      await fetchWorkoutWeekFromDatabase( memberId )
    }
  }

  /**
   * Sessions that were skipped this week.
   */
  const skippedSessions = computed( () =>
    workoutWeek.value.completedSessions.filter( session =>
      // Check if session has a skipped property that is true
      ( session as any ).skipped === true
    )
  )

  /**
   * Workout IDs that have been skipped.
   */
  const skippedWorkoutIds = computed( () =>
    skippedSessions.value.map( session => session.workout.ID )
  )

  /**
   * Remaining workouts for the week are workouts in the program that are in the current week,
   * which includes todaysWorkout, and excludes skipped workouts.
   */
  const remainingWorkoutsThisWeek = computed( () =>
    programStore.program.program?.workouts.filter( workout =>
      workout.weekNumbers.includes( workoutWeek.value.workoutWeekNumber )
        && ! workoutWeek.value.completedSessions.find( session => session.workout.ID === workout.ID )
        && ! skippedWorkoutIds.value.includes( workout.ID )
    )
  )

  /**
   * Determine if the moveToToday value exists in the current workout week.
   */
  const moveToTodayInProgram = computed( () =>
    remainingWorkoutsThisWeek.value?.find( workout => workout.ID === workoutWeek.value.moveToToday )
  )

  /**
   * Get the workout that is scheduled for today.
   */
  const todaysWorkout = computed( () => {
    if ( remainingWorkoutsThisWeek.value ) {
      return workoutWeek.value.moveToToday && moveToTodayInProgram.value
        ? moveToTodayInProgram.value
        : remainingWorkoutsThisWeek.value[0]
    }
    return undefined
  } )

  /**
   * Remaining workouts for the week, excluding today's workout.
   */
  const remainingWorkoutsNotToday = computed( () =>
    remainingWorkoutsThisWeek.value?.filter( workout => workout.ID !== todaysWorkout.value?.ID )
  )

  /**
   * Skipped workouts for the week.
   */
  const skippedWorkoutsThisWeek = computed( () =>
    programStore.program.program?.workouts.filter( workout =>
      skippedWorkoutIds.value.includes( workout.ID )
        && workout.weekNumbers.includes( workoutWeek.value.workoutWeekNumber )
    )
  )

  const resetWorkoutWeek = () => workoutWeek.value = clone( initialWorkoutWeek )

  watch( () => todaysWorkout.value, async ( newValue, oldValue ) => {
    if ( newValue && newValue.ID !== oldValue?.ID ) {
      if ( programStore.program.program && todaysWorkout.value ) {
        console.log( programStore.program.program.ID, todaysWorkout.value.ID )
        const previousSessions = await sessionStore.getPreviousSession( profileStore.profile.ID, todaysWorkout.value.ID, programStore.program.program.ID ) as Session[]
        sessionStore.savedUserAddedSets = await sessionStore.getUserAddedSets( profileStore.profile.ID, todaysWorkout.value.ID )
        handleUserAddedSets( todaysWorkout )
        sessionStore.previousSession = previousSessions[0]
      }
    }
  }, { immediate: true } )

  const handleUserAddedSets = ( todaysWorkout: ComputedRef<Workout | undefined> ) => {
    if ( todaysWorkout.value ) {
      const todaysWorkoutEdit = todaysWorkout.value
      sessionStore.savedUserAddedSets.forEach( userAddedSet => {
        const existingSetGroup = todaysWorkoutEdit.setGroups.find( group => group.setGroup === userAddedSet.setGroup )
        if ( existingSetGroup ) {
          const existingExercise = existingSetGroup.exercises[ userAddedSet.exerciseNumber - 1 ]
          if ( existingExercise ) {
            const totalSets = existingExercise.sets.length
            userAddedSet.sets.forEach( set => {
              if ( set.setNumber > totalSets ) {
                existingExercise.sets.push( {
                  adAmount: 0,
                  rir: null,
                  rest: existingExercise.sets[0].rest,
                  exercise: {
                    ID: existingExercise.ID,
                    title: existingExercise.title,
                    switchedToMain: false,
                  },
                  exerciseNumber: userAddedSet.exerciseNumber,
                  notes: existingExercise.sets[0].notes,
                  reps: 0,
                  rpe: 0,
                  suggestedReps: existingExercise.sets[0].suggestedReps,
                  setGroup: userAddedSet.setGroup,
                  setNotes: [],
                  setNumber: existingExercise.sets.length + 1,
                  setType: todaysWorkoutEdit.setGroups[userAddedSet.setGroup - 1].exercises.length > 1 ? 'super_set' : 'straight_set',
                  completed: false,
                  skipped: false,
                  targetRir: existingExercise.sets[0].targetRir,
                  userAdded: true,
                  weight: 0,
                } )
              } else {
                console.warn( 'set number already exists, skipping:', set.setNumber )
              }
            } )
          }
        }
      } )
    }
  }

  return {
    fetchWorkoutWeekFromDatabase,
    remainingWorkoutsThisWeek,
    remainingWorkoutsNotToday,
    skippedSessions,
    skippedWorkoutsThisWeek,
    resetWorkoutWeek,
    todaysWorkout,
    workoutWeek,
    initializeWorkoutWeek,
  }
} )

export default useStoreWorkoutWeek